import {
  type ReportCardView,
  formatReportCardDate,
  formatReportCardTime,
  reportCardThumbUrl,
} from '../../lib/report_cards';

/**
 * Customer-facing report card renderer (pet report cards, migration 0093).
 *
 * Pure presentational: receives the cards (from `/api/customer/bookings/:id/
 * report-cards` on the account page, or inline from the public live-stay
 * snapshot) and renders each as a branded card — headline mood, notes,
 * photo strip, medication + feeding summary, sign-off. Newest first.
 */

export interface ReportCardListProps {
  cards: ReportCardView[];
  petName?: string | null;
  emptyText?: string;
}

const muted = 'var(--muted-foreground, oklch(0.52 0.008 75))';
const border = '1px solid var(--border, oklch(0.915 0.005 80))';

export function ReportCardList({ cards, petName, emptyText }: ReportCardListProps) {
  if (cards.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: '0.875rem', color: muted }} data-testid="report-cards-empty">
        {emptyText ?? 'No report cards yet — the kennel will send one when there is news to share.'}
      </p>
    );
  }
  return (
    <ul
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '1rem' }}
      data-testid="report-card-list"
    >
      {cards.map((card) => (
        <li
          key={card.id}
          data-testid={`report-card-${card.id}`}
          style={{ border, borderRadius: '12px', padding: '1rem 1.1rem' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '0.75rem',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 500 }}>
              {card.moodLabel ?? `${petName ?? 'Your pet'}'s day`}
            </h3>
            <span className="num" style={{ fontSize: '0.75rem', color: muted }}>
              {card.sentAt ? formatReportCardDate(card.sentAt) : card.reportDate}
            </span>
          </div>

          {card.notes && (
            <p
              style={{
                margin: '0.6rem 0 0',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
              }}
            >
              {card.notes}
            </p>
          )}

          {card.photos.length > 0 && (
            <div
              style={{
                marginTop: '0.75rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                gap: '0.5rem',
              }}
              data-testid={`report-card-${card.id}-photos`}
            >
              {card.photos.map((p) => (
                <img
                  key={p.photoId}
                  src={reportCardThumbUrl(p.fileId)}
                  alt={p.caption ?? 'Photo'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    background: 'oklch(0.95 0.005 75)',
                  }}
                />
              ))}
            </div>
          )}

          {(card.medications.length > 0 || card.feedingNotes) && (
            <dl
              style={{
                margin: '0.75rem 0 0',
                display: 'grid',
                gap: '0.4rem',
                fontSize: '0.8rem',
              }}
            >
              {card.medications.length > 0 && (
                <div>
                  <dt style={{ color: muted, fontWeight: 500 }}>Medication</dt>
                  <dd style={{ margin: 0 }}>
                    {card.medications.map((m) => (
                      <div key={`${m.medicationName}-${m.administeredAt}`}>
                        {m.medicationName}
                        {m.dose ? ` (${m.dose})` : ''} at{' '}
                        <span className="num">{formatReportCardTime(m.administeredAt)}</span>
                      </div>
                    ))}
                  </dd>
                </div>
              )}
              {card.feedingNotes && (
                <div>
                  <dt style={{ color: muted, fontWeight: 500 }}>Feeding</dt>
                  <dd style={{ margin: 0 }}>{card.feedingNotes}</dd>
                </div>
              )}
            </dl>
          )}

          {card.signOff && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', fontStyle: 'italic' }}>
              {card.signOff}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
